import React, { useState, useMemo, memo } from 'react'
import { Button, IconButton, Tooltip, CircularProgress } from '@mui/material'
import { Grading } from '@mui/icons-material'
import { processDataService } from '../../services/processDataService'
import { useProcessContext, useFieldCurrencyMap } from '../../store'
import { getFieldId } from '../../pages/DataEntryPage/utils/utils'
import { ACTIVITY_TYPES, FIELD_TYPES, FORMATS } from '../../utility/constants'
import { fileServices } from '../../services/fileServices'
import { useAuthContext } from '../../hooks/useAuthContext'
import moment from 'moment'
import { extractDisplayName } from '../../utility/fileUtil'
import { formatCurrency } from '../../pages/DataEntryPage/utils/currency'
import LockingTooltip from '../LockingTooltip'
import { formatUserFullName } from '../../utility/userName'
import { replacePlaceholders } from '../../pages/DataEntryPage/utils/placeholderUtil.js'
import { getRecurringPeriod } from '../../utility/datetime.js'

const FieldHistory = ({
  fieldId,
  field,
  children,
  processId = '',
  allValues,
  listDataIdMap: listDataIdMapProp = {},
  listIdsWithPlaceholders: listIdsWithPlaceholdersProp = null,
  dynamicListData = {},
  isPreview = false,
  helperText = null,
  width = '100%',
  rowSuffix = '',
  externalValues = {},
  fieldMappings = {},
}) => {
  const { user } = useAuthContext()
  const processContext = useProcessContext((state) => state.processContext)
  const activityType = processContext?.activityTemplate?.activity_type
  const version = processContext?.processData?.version
  const fieldCurrencyMap = useFieldCurrencyMap((state) => state.fieldCurrencyMap)
  const defaultCurrency = processContext?.companyDetails?.default_currency_code

  // Prefer prop-supplied maps (preview callers), fall back to processContext maps (data-entry)
  const effectiveIdMap = Object.keys(listDataIdMapProp).length
    ? listDataIdMapProp
    : (processContext?.listDataIdMetadataMap ?? {})

  // Data-entry: uses processContext Set. Preview: uses prop-supplied Set.
  // Prop takes precedence so preview callers can supply their own Set.
  const effectivePlaceholderSet =
    (listIdsWithPlaceholdersProp instanceof Set ? listIdsWithPlaceholdersProp : null) ??
    (processContext?.listIdsWithPlaceholders instanceof Set
      ? processContext.listIdsWithPlaceholders
      : null)

  // TableCellWrapper passes fieldId as "123__3" (row embedded) without an explicit
  // rowSuffix prop. Derive the row suffix from fieldId so that replacePlaceholders
  // can use step-1 key lookup (f_<refId>__3) instead of falling back to broad-match
  // which always picks the first key (row 1).
  const effectiveRowSuffix =
    rowSuffix ||
    (() => {
      const dblIdx = String(fieldId).indexOf('__')
      return dblIdx !== -1 ? String(fieldId).slice(dblIdx) : ''
    })()

  const [isOpen, setIsOpen] = useState(false)
  const [historyData, setHistoryData] = useState([])
  const [showHistory, setShowHistory] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [showAllData, setShowAllData] = useState(false)

  /**
   * Returns a slim subset of `allValues` containing only:
   *  - Global/normal field keys: `f_<id>` (no row suffix)
   *  - Current-row field keys:   `f_<id>__<rowIndex>` (matching rowSuffix)
   *
   * This prevents `replacePlaceholders` from scanning thousands of unrelated
   * row keys in a large table. Called lazily inside `renderTooltipContent`
   * so it only executes on hover.
   */
  const getPlaceholderRowData = () => {
    const base = { ...externalValues, ...(processContext?.externalValues ?? {}) }
    if (!allValues) return base
    if (!effectiveRowSuffix) return { ...allValues, ...base }

    const suffix = effectiveRowSuffix // e.g. "__5"
    const keys = Object.keys(allValues)
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      if (!key.startsWith('f_')) {
        base[key] = allValues[key]
        continue
      }
      const dblIdx = key.indexOf('__')
      if (dblIdx === -1) {
        // Global field key (no row suffix) — always include
        base[key] = allValues[key]
      } else if (key.endsWith(suffix)) {
        // Matches the current row — include
        base[key] = allValues[key]
      }
      // Otherwise skip — belongs to a different row
    }
    return base
  }

  const fieldSpecificMap = useMemo(() => {
    const id = field.id
    return (
      fieldMappings[id] ||
      fieldMappings[String(id)] ||
      processContext?.fieldMappings?.[id] ||
      processContext?.fieldMappings?.[String(id)] ||
      field?.metadata?.placeholder_map ||
      {}
    )
  }, [field, fieldMappings, processContext?.fieldMappings])

  /**
   * True only when this field's static list has at least one [[placeholder]] pattern.
   *
   * Uses the `listIdsWithPlaceholders` Set (O(1)). Both data-entry (processContext)
   * and preview (prop) paths now supply a Set, so no entries scan is needed.
   *
   * Dynamic lists (users / teams) never carry placeholders → always false.
   */
  const fieldListHasPlaceholders = useMemo(() => {
    if (
      field.data_structure !== FIELD_TYPES.LIST &&
      field.data_structure !== FIELD_TYPES.LIST_MULTI
    )
      return false
    if (field.list_id == null) return false // dynamic list — no [[]] placeholders

    // O(1) Set lookup — works for both data-entry and preview paths
    if (effectivePlaceholderSet instanceof Set) {
      return effectivePlaceholderSet.has(field.list_id)
    }

    // Neither path supplied a Set yet (e.g. component rendered before load completes)
    return false
  }, [field.id, field.list_id, field.data_structure, effectivePlaceholderSet])

  const handleToggleDataView = () => {
    setShowAllData((prev) => !prev)
  }

  const handleViewHistoryClick = async () => {
    try {
      if (historyData.length > 0) {
        setShowHistory((prev) => !prev)
        return
      }

      setLoadingHistory(true)

      const previousAllReviews = await processDataService.getAllPreviousReviews(
        user,
        fieldId,
        processId,
      )

      const previousAllReviewsNotIncludedCurrent = previousAllReviews.data.filter(
        (review) => review.process_id !== processId,
      )

      setHistoryData(previousAllReviewsNotIncludedCurrent)
      setShowHistory(true)
      setLoadingHistory(false)
    } catch (error) {
      console.error('Error fetching review history:', error)
      setHistoryData([])
      setShowHistory(false)
      setLoadingHistory(false)
    }
  }

  const getReviewedData = (revDatum) => {
    if (!revDatum?.data) return null

    const ReviewStatus =
      revDatum?.review_data === true ? (
        <span style={{ fontSize: '9px', marginLeft: '9px' }}> &#9989;</span>
      ) : revDatum?.review_data === false ? (
        <span style={{ fontSize: '9px', marginLeft: '9px' }}> &#10060;</span>
      ) : null

    const listItem = getListActualValue(
      field,
      fieldId,
      allValues,
      dynamicListData,
      effectiveRowSuffix,
      undefined, // placeholderRowData: built lazily inside getListActualValue
    )

    switch (field.data_structure) {
      case FIELD_TYPES.LIST:
      case FIELD_TYPES.LIST_MULTI:
        return listItem ? (
          <>
            <strong>{listItem}</strong> {ReviewStatus}
          </>
        ) : null

      case FIELD_TYPES.DATE:
        return (
          <>
            <strong>{moment(revDatum.data).format(FORMATS.DATE)}</strong> {ReviewStatus}
          </>
        )

      case FIELD_TYPES.DATETIME:
        return (
          <>
            <strong>{moment(revDatum.data).format(FORMATS.DATETIME)}</strong> {ReviewStatus}
          </>
        )

      case FIELD_TYPES.CURRENCY: {
        const currencyCode = fieldCurrencyMap[field.id] ?? defaultCurrency ?? 'INR'
        const revFormattedValue = formatCurrency(revDatum.data, currencyCode, {
          defaultCurrency: processContext?.companyDetails?.default_currency_code,
        })

        return (
          <>
            <strong>{revFormattedValue}</strong> {ReviewStatus}
          </>
        )
      }
      case FIELD_TYPES.FILE:
        return (
          <span style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>
              {' '}
              <strong>{extractDisplayName(revDatum.data)}</strong> {ReviewStatus}
            </span>
            <Button
              onClick={() => fileServices.downloadFile(user, revDatum.data)}
              variant="outlined"
              style={{
                height: '19px',
                width: '20px',
                color: 'white',
                borderColor: 'white',
                marginLeft: '12px',
              }}
            >
              &#11015;
            </Button>
          </span>
        )
      case FIELD_TYPES.TIMESTAMPED_BIGTEXT:
      case FIELD_TYPES.TIMESTAMPED_DATE:
      case FIELD_TYPES.TIMESTAMPED_DATETIME: {
        const timestampedValues = revDatum.data || []
        let formattedText = timestampedValues.map((item, index) => {
          const user = dynamicListData?.users?.find((u) => u.id === item?.user_id)
          const username = user ? formatUserFullName(user) : null

          const lines = item.text.split('\n').map((line, i) => (
            <span key={`${index}-${i}`}>
              {line}
              <br />
            </span>
          ))

          return (
            <div key={index}>
              <i>{moment(item.timestamp).format(FORMATS.DATETIME)}:</i> {lines}{' '}
              {username ? ` - ${username}` : ''}
            </div>
          )
        })

        return (
          <div>
            {formattedText} {ReviewStatus}
          </div>
        )
      }

      default:
        return (
          <>
            <strong>{revDatum.data}</strong> {ReviewStatus}
          </>
        )
    }
  }

  const getListActualValue = (
    field,
    fieldId,
    allValues,
    dynamicListData,
    rowSuffix = '',
    placeholderRowData = null,
  ) => {
    if (!field || !fieldId || !allValues) return ''
    const fieldKey = getFieldId(fieldId)
    const fieldValue = allValues[fieldKey]

    if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) return ''

    if (field.list_id == null) {
      const type = field?.dynamic_field_type?.type
      const listData =
        type === 'users'
          ? isPreview
            ? dynamicListData?.users
            : dynamicListData?.usersList
          : isPreview
            ? dynamicListData?.teams
            : dynamicListData?.teamsList

      if (Array.isArray(fieldValue)) {
        const values = fieldValue
          .map((id) => {
            const item = listData?.find((it) => it.id == id)
            if (!item) return null
            if (type === 'users') {
              return formatUserFullName(item)
            } else if (type === 'teams') {
              return item.name || item.value || ''
            }
            return item.value || ''
          })
          .filter(Boolean)
        return (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '6px', lineHeight: '1.4em' }}
          >
            {values.map((val, index) => (
              <div key={index}>{val}</div>
            ))}
          </div>
        )
      } else {
        const item = listData?.find((it) => it.id == fieldValue)
        if (!item) return ''
        if (type === 'users') {
          return formatUserFullName(item)
        } else if (type === 'teams') {
          return item.name || item.value || ''
        }
        return item.value || ''
      }
    }

    // Handle static list (LIST or LIST_MULTI)
    // Resolve rowData lazily — only needed when placeholders are present.
    // `placeholderRowData` can be:
    //   object  → pre-built by renderTooltipContent (hover path)
    //   null    → build lazily now (getReviewedData path)
    const resolveRowData = () => placeholderRowData ?? getPlaceholderRowData()

    if (Array.isArray(fieldValue)) {
      // LIST_MULTI
      const values = fieldValue
        .map((id) => {
          // O(1) lookup — key is list_data_id (data-entry) or id (preview)
          const entry = effectiveIdMap[id]

          const listDataValue = entry?.list_data_value || entry?.value || null
          // Skip expensive replacement when this list has no [[...]] patterns
          if (!fieldListHasPlaceholders) return listDataValue
          return replacePlaceholders(
            listDataValue,
            resolveRowData(),
            effectiveIdMap,
            fieldSpecificMap,
            rowSuffix,
            processContext?.fieldMetadata ?? {},
          )
        })
        .filter(Boolean)
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            lineHeight: '1.6em',
            lineBreak: 'anywhere',
            gap: '8px',
          }}
        >
          {values.map((val, index) => (
            <div key={index}>{val}</div>
          ))}
        </div>
      )
    } else {
      // LIST — O(1) lookup
      const entry = effectiveIdMap[fieldValue]

      const listDataValue = entry?.list_data_value || entry?.value || ''
      // Skip expensive replacement when this list has no [[...]] patterns
      if (!fieldListHasPlaceholders) return listDataValue
      return replacePlaceholders(
        listDataValue,
        resolveRowData(),
        effectiveIdMap,
        fieldSpecificMap,
        rowSuffix,
        processContext?.fieldMetadata ?? {},
      )
    }
  }

  const renderTooltipContent = () => {
    if (!isOpen) return null

    // Build the slim rowData slice on hover only — avoids scanning thousands of
    // unrelated keys from other table rows during normal (non-hover) renders.
    // Further: skip the scan entirely when this field's list has no [[...]] patterns.
    const placeholderRowData = fieldListHasPlaceholders ? getPlaceholderRowData() : null

    const value = allValues[getFieldId(fieldId)]
    const currencyCode = fieldCurrencyMap[field.id] ?? defaultCurrency ?? 'INR'

    let formattedValue

    if (field.data_structure === FIELD_TYPES.CURRENCY) {
      formattedValue = formatCurrency(value, currencyCode, {
        defaultCurrency: processContext?.companyDetails?.default_currency_code,
      })
    } else {
      formattedValue = value || 'N/A'
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          rowGap: '5px',
          overflowY: 'auto',
          padding: '10px !important',
          maxHeight: '370px',
        }}
      >
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '12px' }}>
              <span
                style={{
                  fontWeight: showHistory && 'bolder',
                }}
              >
                {field.field_template_name}:{' '}
              </span>

              {helperText ? (
                helperText
              ) : (
                <span>
                  {field.data_structure === FIELD_TYPES.LIST ||
                  field.data_structure === FIELD_TYPES.LIST_MULTI ? (
                    <span style={{ whiteSpace: 'pre-line' }}>
                      {getListActualValue(
                        field,
                        fieldId,
                        allValues,
                        dynamicListData,
                        effectiveRowSuffix,
                        placeholderRowData,
                      ) || 'Please select an option'}
                    </span>
                  ) : field.data_structure === FIELD_TYPES.DATE &&
                    allValues[getFieldId(fieldId)] ? (
                    <>
                      {field.is_recurring_subfield
                        ? getRecurringPeriod(allValues[getFieldId(fieldId)], field.frequency)
                            .rangeText
                        : moment(allValues[getFieldId(fieldId)]).format(FORMATS.DATE)}
                    </>
                  ) : field.data_structure === FIELD_TYPES.DATETIME &&
                    allValues[getFieldId(fieldId)] ? (
                    <>
                      {field.is_recurring_subfield
                        ? getRecurringPeriod(allValues[getFieldId(fieldId)], field.frequency)
                            .rangeText
                        : moment(allValues[getFieldId(fieldId)]).format(FORMATS.DATETIME)}
                    </>
                  ) : field.data_structure === FIELD_TYPES.FILE &&
                    allValues[getFieldId(fieldId)] ? (
                    <>{extractDisplayName(allValues[getFieldId(fieldId)])}</>
                  ) : field.data_structure === FIELD_TYPES.CURRENCY &&
                    allValues[getFieldId(fieldId)] ? (
                    <> {formattedValue}</>
                  ) : [
                      FIELD_TYPES.TIMESTAMPED_BIGTEXT,
                      FIELD_TYPES.TIMESTAMPED_DATE,
                      FIELD_TYPES.TIMESTAMPED_DATETIME,
                    ].includes(field.data_structure) ? (
                    <>
                      {!value || !value.length ? (
                        <div>No entries yet.</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          {value.map((v, index) => {
                            const user = dynamicListData?.users?.find((u) => u.id === v.user_id)
                            const username = user ? formatUserFullName(user) : null

                            return (
                              <div
                                key={index}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  padding: '8px 12px',
                                  gap: '6px',
                                }}
                              >
                                <div
                                  style={{ flex: '0 0 120px', fontSize: '11px', fontWeight: 600 }}
                                >
                                  {v.timestamp}
                                </div>

                                <div style={{ flex: 1, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                                  {v.text}
                                  {username && (
                                    <span
                                      style={{
                                        fontSize: '11px',
                                        fontStyle: 'italic',
                                        color: '#888',
                                        marginLeft: 6,
                                      }}
                                    >
                                      - {username}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <span style={{ whiteSpace: 'pre-line' }}>{allValues[getFieldId(fieldId)]}</span>
                  )}
                </span>
              )}
            </span>
            <span style={{ marginTop: '-5px' }}>
              {showHistory &&
                !(
                  (activityType === ACTIVITY_TYPES.DATA_ENTRY && version == 1) ||
                  field.is_computed ||
                  field.is_read_only
                ) && (
                  <Tooltip title={showAllData ? 'hide reviews' : 'show reviews'}>
                    <IconButton onClick={handleToggleDataView}>
                      <Grading style={{ fontSize: '16px' }} />
                      {showAllData && (
                        <span
                          style={{
                            position: 'absolute',
                            top: '46%',
                            left: '6px',
                            width: '60%',
                            height: '2px',
                            backgroundColor: 'red',
                            transform: 'rotate(-45deg)',
                          }}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
            </span>
          </div>
          {showHistory && <hr style={{ margin: '0px 0px 10px 0px' }} />}
        </div>

        {loadingHistory ? (
          <CircularProgress size={20} color="primary" />
        ) : (
          showHistory &&
          historyData?.length > 0 &&
          historyData
            .filter(
              (revDatum) => showAllData || revDatum.activity_type === ACTIVITY_TYPES.DATA_ENTRY,
            )
            .map((revDatum, index, filteredArray) => (
              <div key={index}>
                <p style={{ margin: 0, whiteSpace: 'pre-line' }}>{getReviewedData(revDatum)}</p>

                <p style={{ margin: 0 }}>
                  {revDatum?.user_name?.length > 0 ? (
                    <>{revDatum?.user_name}</>
                  ) : (
                    <>Not reviewed yet!!</>
                  )}{' '}
                </p>

                {revDatum?.activity_type == ACTIVITY_TYPES.REVIEW ? (
                  <p style={{ margin: 0 }}>
                    Review of &quot;{revDatum?.associated_data_entry_activity_template_name}&quot;
                    version {revDatum?.version}
                  </p>
                ) : (
                  <p style={{ margin: 0 }}>
                    Activity: &quot;{revDatum?.activity_template_name}&quot; version{' '}
                    {revDatum?.version}
                  </p>
                )}

                {revDatum?.activity_type === ACTIVITY_TYPES.REVIEW && (
                  <p style={{ margin: 0, whiteSpace: 'pre-line' }}>
                    Review Comment: {revDatum?.review_comments_data}
                  </p>
                )}

                {revDatum?.activity_type == ACTIVITY_TYPES.REVIEW ? (
                  <p style={{ justifyContent: 'end', margin: 0 }}>
                    Review date: {moment(revDatum?.modified_at).format(FORMATS.DATETIME)}
                  </p>
                ) : (
                  <p style={{ justifyContent: 'end', margin: 0 }}>
                    Date: {moment(revDatum?.modified_at).format(FORMATS.DATETIME)}
                  </p>
                )}
                {index !== filteredArray.length - 1 && <hr />}
              </div>
            ))
        )}
        {!showHistory &&
          !loadingHistory &&
          !(
            (activityType === ACTIVITY_TYPES.DATA_ENTRY && version == 1) ||
            field.is_computed ||
            field.is_read_only
          ) && (
            <span
              onClick={handleViewHistoryClick}
              size="small"
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => (e.target.style.color = 'gray')}
              onMouseLeave={(e) => (e.target.style.color = null)}
            >
              View history
            </span>
          )}
      </div>
    )
  }

  return (
    <LockingTooltip
      style={{ width: width ? width : '100%' }}
      slotProps={{
        popper: {
          sx: {
            '& .MuiTooltip-tooltip': {
              minWidth: showHistory ? '375px !important' : 'auto',
              maxWidth: '475px',
              whiteSpace: 'normal',
              border: 'none !important',
            },
          },
        },
      }}
      placement="bottom"
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      title={renderTooltipContent()}
    >
      <span className={`cell-value-inside-field-history ${isPreview ? 'table-context' : ''}`}>
        {children}
      </span>
    </LockingTooltip>
  )
}

const areElementsEqual = (a, b) => {
  if (a === b) return true
  if (a == null || b == null) return a === b

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      if (!areElementsEqual(a[i], b[i])) return false
    }
    return true
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const isReactA = a.$$typeof != null
    const isReactB = b.$$typeof != null
    if (isReactA !== isReactB) return false
    if (isReactA && isReactB) {
      if (a.$$typeof !== b.$$typeof) return false
      if (a.type !== b.type) return false
      if (a.key !== b.key) return false

      const propsA = a.props || {}
      const propsB = b.props || {}
      const keysA = Object.keys(propsA)
      const keysB = Object.keys(propsB)

      if (keysA.length !== keysB.length) return false
      for (const key of keysA) {
        if (key === 'children') {
          if (!areElementsEqual(propsA.children, propsB.children)) return false
        } else if (typeof propsA[key] === 'function' && typeof propsB[key] === 'function') {
          continue
        } else if (propsA[key] !== propsB[key]) {
          return false
        }
      }
      return true
    }

    const keysA = Object.keys(a)
    const keysB = Object.keys(b)
    if (keysA.length !== keysB.length) return false
    for (const key of keysA) {
      if (typeof a[key] === 'function' && typeof b[key] === 'function') {
        continue
      }
      if (a[key] !== b[key]) return false
    }
    return true
  }

  return false
}

export default memo(FieldHistory, (prevProps, nextProps) => {
  return (
    prevProps.fieldId === nextProps.fieldId &&
    prevProps.field === nextProps.field &&
    prevProps.width === nextProps.width &&
    prevProps.helperText === nextProps.helperText &&
    prevProps.isPreview === nextProps.isPreview &&
    prevProps.processId === nextProps.processId &&
    prevProps.rowSuffix === nextProps.rowSuffix &&
    areElementsEqual(prevProps.children, nextProps.children) &&
    prevProps.externalValues === nextProps.externalValues &&
    prevProps.fieldMappings === nextProps.fieldMappings &&
    prevProps.listDataIdMap === nextProps.listDataIdMap &&
    prevProps.allValues?.[getFieldId(prevProps.fieldId)] ===
      nextProps.allValues?.[getFieldId(nextProps.fieldId)]
  )
})
